import { useEffect, useRef } from 'react';

import { setCurrentScreen } from '../../screenContext';
import { trackingService } from '../../services';

/**
 * The catalogued universal screen-view event. One event for every screen in
 * every app — the screen is a **property**, not a name — so a funnel can be
 * re-cut without new instrumentation (catalog: `events.ui.screen_displayed`).
 */
export const SCREEN_DISPLAYED_EVENT = 'screen_displayed';

/**
 * Emits `screen_displayed` with `screen_name` once per displayed screen.
 *
 * Deliberately calls the **base** tracking service rather than an app's typed
 * wrapper: the wrappers prefix every event with the app name (`Handyparken: …`),
 * and this event must carry the identical name in all apps for a cross-app
 * funnel to exist. The app is already on the event via the `app` super property.
 *
 * `screen_name` values belong to the per-app block of `registries.screen_name`
 * in the catalog; pass them through an app-level union so a typo fails the build.
 *
 * Fires again when `screenName` changes (one component rendering several
 * screens), never twice for the same value, and not at all while it is empty —
 * so a screen that resolves its name asynchronously can call the hook from the
 * first render.
 */
export const useTrackScreenDisplayed = (
  screenName: string | null | undefined,
) => {
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    // Going nullish means the screen stopped being displayed, so forget it:
    // coming back to it is a second view, not the same one. Without this a tab
    // that is un-mounted-but-kept (vas-v2 gates its three tabs on `isCurrent`)
    // is reported once and never again, while its legacy event — a plain effect
    // — keeps firing on every return. The two series would then diverge on the
    // commonest interaction in the app.
    if (!screenName) {
      lastTracked.current = null;
      setCurrentScreen(null);

      return;
    }

    if (lastTracked.current === screenName) {
      return;
    }

    lastTracked.current = screenName;
    // Every UI event that follows is labelled with this screen; see
    // `screenContext` for why it is remembered here rather than passed around.
    setCurrentScreen(screenName);

    trackingService.trackEvent(SCREEN_DISPLAYED_EVENT, {
      screen_name: screenName,
    });
  }, [screenName]);
};

export default useTrackScreenDisplayed;
